// js/custom-element/section/product-list/quick-add-modal.js
if (!customElements.get('quick-add-modal')) {
  class QuickAddModal extends CustomHTMLElement {
    connectedCallback() {
      this.productId = this.getAttribute('product-id');
      this.productHandle = this.getAttribute('product-handle');
      this.sectionId = this.getAttribute('section-id');
      this.blockId = this.getAttribute('block-id');

      this.overlay = this.querySelector('.quick-add-modal__overlay');
      this.content = this.querySelector('.quick-add-modal__content');
      this.ctaButton = this.querySelector('.quick-add-modal__cta');
      this.closeButton = this.querySelector('.quick-add-modal__close');

      this.selectedOptions = {}; // Track option1, option2, option3
      this.selectedVariantId = null;
      this.productData = null;

      this.delegate.on('click', '.quick-add-modal__overlay', this._close.bind(this));
      this.delegate.on('click', '.quick-add-modal__close', this._close.bind(this));
      this.delegate.on('click', '.quick-add-modal__variant-button', this._onOptionButtonClick.bind(this));
      this.delegate.on('click', '.quick-add-modal__cta', this._onCtaClick.bind(this));

      // Load product data
      this._loadProductData();
    }

    async _loadProductData() {
      try {
        this.productData = await ProductLoader.load(this.productHandle);
      } catch (error) {
        console.error('Failed to load product data:', error);
      }
    }

    open() {
      // Pre-select color from swatch (if available)
      this._preSelectColor();

      this.setAttribute('open', '');
      document.body.style.overflow = 'hidden';

      // Update availability based on pre-selected color
      this._updateButtonAvailability();

      // Animate in
      this.overlay.style.opacity = '0';
      this.content.style.transform = 'translateY(100%)';

      requestAnimationFrame(() => {
        this.overlay.style.transition = 'opacity 0.3s ease';
        this.content.style.transition = 'transform 0.3s ease';
        this.overlay.style.opacity = '1';
        this.content.style.transform = 'translateY(0)';
      });
    }

    _preSelectColor() {
      const colorPosition = this.getAttribute('data-color-option-position');
      if (!colorPosition) return; // No color option

      const defaultColorValue = this.getAttribute('data-default-color-value');

      // Try to find the product item to check for selected color swatch
      const productItem = this.previousElementSibling;
      if (productItem && productItem.tagName === 'PRODUCT-ITEM') {
        const colorSwatchList = productItem.querySelector('.color-swatch-list');
        if (colorSwatchList) {
          const checkedSwatch = colorSwatchList.querySelector('input[type="radio"]:checked');
          if (checkedSwatch) {
            // Use the selected color from swatch
            this.selectedOptions[`option${colorPosition}`] = checkedSwatch.value;
            return;
          }
        }
      }

      // Fall back to default color
      if (defaultColorValue) {
        this.selectedOptions[`option${colorPosition}`] = defaultColorValue;
      }
    }

    _close() {
      this.overlay.style.opacity = '0';
      this.content.style.transform = 'translateY(100%)';

      setTimeout(() => {
        this.removeAttribute('open');
        document.body.style.overflow = '';

        // Reset selections
        this.selectedOptions = {};
        this.selectedVariantId = null;
        this.querySelectorAll('.quick-add-modal__variant-button').forEach(btn => btn.removeAttribute('selected'));
        this.ctaButton.setAttribute('disabled', '');
      }, 300);
    }

    _onOptionButtonClick(event, target) {
      const button = target.closest('.quick-add-modal__variant-button');
      if (!button || button.hasAttribute('disabled')) return;

      const position = button.getAttribute('data-option-position');
      const value = button.getAttribute('data-option-value');

      // Store selected option
      this.selectedOptions[`option${position}`] = value;

      // Update button states for this option group
      const optionGroup = button.closest('.quick-add-modal__option-group');
      optionGroup.querySelectorAll('.quick-add-modal__variant-button').forEach(btn => {
        if (btn === button) {
          btn.setAttribute('selected', '');
        } else {
          btn.removeAttribute('selected');
        }
      });

      // Find matching variant
      this.selectedVariantId = this._findVariantByOptions();

      // Update button availability
      this._updateButtonAvailability();

      // Update CTA
      if (this.selectedVariantId) {
        this.ctaButton.removeAttribute('disabled');
        this._updateCtaText();
      } else {
        this.ctaButton.setAttribute('disabled', '');
      }
    }

    _findVariantByOptions() {
      if (!this.productData) return null;

      // Find variant that matches all selected options
      const variant = this.productData.variants.find(variant => {
        return Object.keys(this.selectedOptions).every(optionKey => {
          return variant[optionKey] === this.selectedOptions[optionKey];
        });
      });

      return variant ? variant.id : null;
    }

    _updateButtonAvailability() {
      if (!this.productData) return;

      // For each option group, check which values lead to available variants
      this.querySelectorAll('.quick-add-modal__option-group').forEach((group, groupIndex) => {
        const buttons = group.querySelectorAll('.quick-add-modal__variant-button');

        buttons.forEach(button => {
          const position = button.getAttribute('data-option-position');
          const value = button.getAttribute('data-option-value');

          // Check if there's any available variant with this option value
          // combined with already selected options from other groups
          const hasAvailableVariant = this.productData.variants.some(variant => {
            // Check if variant matches this option value
            if (variant[`option${position}`] !== value) return false;

            // Check if variant matches other selected options
            const matchesOtherSelections = Object.keys(this.selectedOptions).every(optionKey => {
              // Skip the current option position
              if (optionKey === `option${position}`) return true;
              return variant[optionKey] === this.selectedOptions[optionKey];
            });

            return matchesOtherSelections && variant.available;
          });

          if (!hasAvailableVariant) {
            button.setAttribute('disabled', '');
          } else {
            button.removeAttribute('disabled');
          }
        });
      });
    }

    _updateCtaText() {
      if (!this.productData) return;

      // Always show "ADD TO CART" when variant is selected
      this.ctaButton.textContent = 'ADD TO CART';
    }

    async _onCtaClick() {
      if (!this.selectedVariantId || !this.productData) return;

      // Always add to cart
      await this._addToCart();
    }

    async _addToCart() {
      try {
        const formData = new FormData();
        formData.append('id', this.selectedVariantId);
        formData.append('quantity', '1');

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Cart add failed:', response.status, errorText);
          throw new Error(`Failed to add to cart: ${response.status}`);
        }

        const data = await response.json();

        // 1. Dispatch variant:added event
        document.dispatchEvent(new CustomEvent('variant:added', {
          bubbles: true,
          detail: {
            variant: data.hasOwnProperty('items') ? data.items[0] : data
          }
        }));

        // 2. Fetch updated cart
        const cartResponse = await fetch('/cart.js');
        const cartContent = await cartResponse.json();

        // 3. Dispatch cart:updated event
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            cart: cartContent
          }
        }));

        // 4. Dispatch cart:refresh event with openMiniCart
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: window.themeVariables?.settings?.cartType === 'drawer'
          }
        }));

        // Close modal
        this._close();
      } catch (error) {
        console.error('Failed to add to cart:', error);
        alert('Sorry, there was an error adding this product to your cart. Please try again.');
      }
    }
  }

  window.customElements.define('quick-add-modal', QuickAddModal);

  // Global event listener for modal trigger buttons
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('.quick-add-modal__trigger');
    if (!trigger) return;

    const modalId = trigger.getAttribute('data-modal-id');
    if (!modalId) return;

    const modal = document.getElementById(modalId);
    if (modal && modal.open) {
      modal.open();
    }
  });

  // Global event listener for single-variant quick add buttons
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('.product-item__quick-add-single');
    if (!button) return;

    let variantId = button.getAttribute('data-variant-id');
    const productHandle = button.getAttribute('data-product-handle');
    const useColorSwatch = button.getAttribute('data-use-color-swatch');

    // For color-only products, get variant from selected color swatch
    if (useColorSwatch === 'true') {
      const productItem = button.closest('product-item');
      const colorSwatchList = productItem?.querySelector('.color-swatch-list');

      if (colorSwatchList) {
        const checkedSwatch = colorSwatchList.querySelector('input[type="radio"]:checked');
        if (checkedSwatch) {
          variantId = checkedSwatch.getAttribute('data-variant-id');
        }
      }
    }

    if (!variantId) return;

    // Disable button during request
    button.disabled = true;
    const originalContent = button.innerHTML;
    button.classList.add('is-loading');

    try {
      // Add to cart
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Cart add failed:', response.status, errorText);
        throw new Error(`Failed to add to cart: ${response.status}`);
      }

      const data = await response.json();

      // 1. Dispatch variant:added event
      document.dispatchEvent(new CustomEvent('variant:added', {
        bubbles: true,
        detail: {
          variant: data.hasOwnProperty('items') ? data.items[0] : data
        }
      }));

      // 2. Fetch updated cart
      const cartResponse = await fetch('/cart.js');
      const cartContent = await cartResponse.json();

      // 3. Dispatch cart:updated event
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: {
          cart: cartContent
        }
      }));

      // 4. Dispatch cart:refresh event with openMiniCart
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart: cartContent,
          openMiniCart: window.themeVariables?.settings?.cartType === 'drawer'
        }
      }));

    } catch (error) {
      console.error('Failed to add to cart:', error);
      alert('Sorry, there was an error adding this product to your cart. Please try again.');
    } finally {
      // Re-enable button
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  });
}
